import { AbstractControl, FormControl, FormGroup, ValidationErrors } from "@angular/forms";
import * as dayjs from 'dayjs';
import './augmentations/form-control';

export const matchingValues = (controlName: string, matchingControlName: string) => {
  return (formGroup: FormGroup) => {
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];
    if (matchingControl.errors && !matchingControl.errors['matchingValues']) {
      return;
    }
    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ matchingValues: true });
    } else {
      matchingControl.setErrors(null);
    }
  };
};

export const timePattern: string = '^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$';

export const passwordRegex: RegExp = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).+$/;
export const passwordLength: number = 8;


export const sequentialMonths = (firstDateControl: string, secondDateControl: string) => {
  return (formGroup: FormGroup) => {
    const month1 = formGroup.controls[firstDateControl] as FormGroup;
    const month2 = formGroup.controls[secondDateControl] as FormGroup;

    const month1Parsed = dayjs(`2021-${ month1.controls['month'].value }-${ month1.controls['date'].value } `);
    const month2Parsed = dayjs(`2021-${ month2.controls['month'].value }-${ month2.controls['date'].value } `);
    if (month2.errors && !month2.errors['sequentialMonths']) {
      return;
    }
    if (month1Parsed > month2Parsed) {
      month2.setErrors({ sequentialMonths: true });
    } else {
      month2.setErrors(null);
    }
  };
};


export const sequentialTimes = (timeFrom: string, timeTo: string) => {
  return (formGroup: FormGroup) => {
    const timeFromControl = formGroup.controls[timeFrom];
    const timeToControl = formGroup.controls[timeTo];

    const timeFromParsed = dayjs(`2021-01-01 ${ timeFromControl.value }`);
    const timeToParsed = dayjs(`2021-01-01 ${ timeToControl.value }`);
    if (timeToControl.errors && !timeToControl.errors['sequentialTimes']) {
      return;
    }
    if (timeFromParsed > timeToParsed) {
      timeToControl.setErrors({ sequentialTimes: true });
    } else {
      timeToControl.setErrors(null);
    }
  };
};

export const fileSizeValidator = (sizeInKb: number) => {
  return (control: FormControl): { [key: string]: any } | null => {
    const maxSizeInMB = +(sizeInKb / 1024).toFixed(2);
    control.pvscAdditionalData.fileSizeInMB = maxSizeInMB;
    if (!control.value || !(control.value[0] instanceof File)) {
      return null;
    }
    const files = control.value as File[];
    for (const file of files) {
      const fileSizeInKB = Math.round(file.size / 1024);
      if (fileSizeInKB >= sizeInKb) {
        return {
          fileSize: true,
          acceptedFileSizeInMB: maxSizeInMB,
        };
      }
    }
    return null;
  };
};

export const fileTypeValidator = (acceptedTypes: string[]) => {
  return (control: FormControl): { [key: string]: any } | null => {
    control.pvscAdditionalData.fileType = acceptedTypes.join(', ');
    if (!control.value || !(control.value[0] instanceof File)) {
      return null;
    }
    const files = control.value as File[];
    const typesLowercase = acceptedTypes.map(type => type.toLowerCase());
    for (const file of files) {
      const extension: string = file.name.split('.').pop()?.toLowerCase() as string;
      if (!typesLowercase.includes(extension)) {
        return {
          fileType: true,
          acceptedFileTypes: acceptedTypes.join(', '),
        };
      }
    }
    return null;
  };
};


const isValidOIB = (oib: string): boolean => {
  if (!/^[0-9]{11}$/.test(oib))
    return false;

  let a = 10;
  for (const digit of oib.substring(0, 10)) {
    a += parseInt(digit);
    a %= 10;
    if (a === 0)
      a = 10;
    a *= 2;
    a %= 11;
  }

  let kontrolni = 11 - a;
  if (kontrolni === 10)
    kontrolni = 0;

  return kontrolni === parseInt(oib.substring(10, 11));
};

export const OIBvalidator = (control: AbstractControl): ValidationErrors | null => {
  if (!isValidOIB(control.value)) {
    return {
      invalidOIB: true,
    };
  }
  return null;
};

export const PDVvalidator = (control: AbstractControl): ValidationErrors | null => {
  const isNumeric = /^\d+$/.test(control.value);
  const isValidLength = control.value.length === 13;

  if (!isNumeric || !isValidLength) {
    return {
      invalidPDV: true,
    };
  }

  return null;
};


export const moreThanAndNotEqualNumber = (num: number) => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isNaN(+control.value) || +control.value <= num) {
      return {
        moreThan: num,
      };
    }
    return null;
  };
};

export const lessThanAndNotEqualNumber = (num: number) => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isNaN(+control.value) || +control.value >= num) {
      return {
        lessThan: num,
      };
    }
    return null;
  };
};

export const between = (min: number, max: number) => {
  return (control: AbstractControl): ValidationErrors | null => {
    const numValue = +control.value;
    if (isNaN(numValue) || numValue < min || numValue > max) {
      return {
        between: {
          min: min,
          max: max,
        },
      };
    }
    return null;
  };
}
